import React from 'react';
import {
  cycleToEnglish,
  type OfferDiscountInfo,
} from '@/epics/subscriptionManagment/hooks/useCreateSubscription';
import { StatSummaryCard } from '@/shared/components/StatSummaryCard';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';
import { getCycleSuffix } from '@/epics/subscriptionManagment/utils/pricing';

export interface AppliedOfferCardProps {
  planName: string;
  price: string;
  cycle: string;
  /** No offer selected yet is a normal state — the card still shows the
   * plan's own price/cycle, just without a discount badge. */
  discount?: OfferDiscountInfo | null;
  formatPriceWithSpaces: (price: string) => string;
}

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

/**
 * Price/cycle preview shown on Create Subscription Plan once price and
 * billing cycle are filled in — matches the Pozytron_Internal Figma file's
 * "Applied Offer" component (node 91835:606723) once an offer is selected;
 * without one, it's the same layout minus the discount badge, Final Price
 * equal to Base Price (per PBR: "On selection of offer, the badge and final
 * price will be updated" — the table itself doesn't require an offer).
 */
export const AppliedOfferCard: React.FC<AppliedOfferCardProps> = ({
  planName,
  price,
  cycle,
  discount,
  formatPriceWithSpaces,
}) => {
  const basePrice =
    parseFloat((price || '0').replace(/\s/g, '').replace(',', '.')) || 0;
  const finalPrice = discount
    ? discount.discountType === 'PERCENTAGE'
      ? basePrice * (1 - discount.discountValue / 100)
      : Math.max(0, basePrice - discount.discountValue)
    : basePrice;

  const cycleEn = cycleToEnglish(cycle);
  const billingCycleLabel = capitalize(cycle);
  const periodSuffix = getCycleSuffix(cycleEn);

  const badgeText = discount
    ? discount.discountType === 'PERCENTAGE'
      ? `${discount.discountValue}% off`
      : `${formatPriceWithSpaces(discount.discountValue.toFixed(2))} zł off`
    : undefined;

  return (
    <StatSummaryCard
      title={planName || 'This plan'}
      badge={
        badgeText ? (
          <span className="bg-information-40 text-white text-labelMedium rounded-full px-2 py-0.5">
            {badgeText}
          </span>
        ) : undefined
      }
      rows={[
        [
          {
            label: SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE.polish,
            value: `${formatPriceWithSpaces(basePrice.toFixed(2))} zł`,
          },
          {
            label: SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BILLING_CYCLE.polish,
            value: billingCycleLabel,
          },
          {
            label: 'Final Price',
            value: (
              <>
                {formatPriceWithSpaces(finalPrice.toFixed(2))}{' '}
                <span className="font-normal">zł{periodSuffix}</span>
              </>
            ),
            valueClassName: 'text-labelLarge text-information-30',
          },
        ],
      ]}
    />
  );
};

export default AppliedOfferCard;
